import Reveal from "@/components/Reveal";
import { IMAGES, BRAND } from "@/lib/constants";

const PRINCIPLES = [
  { no: "01", title: "Transparency", desc: "Full reporting, milestone updates, no hidden fees. Investors always know where their capital is and how it is performing." },
  { no: "02", title: "Discipline", desc: "Every deal is underwritten conservatively. We pass on more opportunities than we accept — because losing capital is unforgivable." },
  { no: "03", title: "Craft", desc: "From distressed heritage flips in Edmonton to luxury villas in the Caribbean, we hold every build to a standard we would live in." },
  { no: "04", title: "Access", desc: "A cross-border network of contractors, surveyors, legal partners, and municipal contacts — the unfair advantage you inherit as our investor." },
];

const TIMELINE = [];
export default function About({ onOpenConsult }) {
  return (
    <div data-testid="about-page" className="bg-[#0A1628]">
      {/* HEADER */}
      <section data-testid="about-header" className="relative pt-40 pb-24 md:pb-32 px-6 md:px-12 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.skyline} alt="Skyline" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0A1628]/70 to-[#0A1628]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-2">
            <div className="overline">About</div>
          </div>
          <div className="lg:col-span-10">
            <h1 className="font-serif-display text-6xl md:text-8xl lg:text-[9rem] text-[#F3EFE4] leading-[0.88]">
              A trusted global partner<br />
              in real estate <span className="italic text-[#D4AF37]">development.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section data-testid="about-mission" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12 md:gap-20">
          <Reveal>
            <div className="border-l-2 border-[#D4AF37] pl-8">
              <div className="overline mb-6">Our mission</div>
              <p className="font-serif-display text-2xl md:text-3xl lg:text-4xl text-[#F3EFE4] leading-[1.15]">
                {BRAND.mission}
              </p>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="border-l-2 border-[#D4AF37] pl-8">
              <div className="overline mb-6">Our vision</div>
              <p className="font-serif-display text-2xl md:text-3xl lg:text-4xl text-[#F3EFE4] leading-[1.15]">
                {BRAND.vision}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />


      {/* STORY */}
      <section data-testid="about-story" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-6">Our story</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#F3EFE4] leading-[0.95]">
              From a single Edmonton infill to luxury villas in the Caribbean.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 text-[#8FA3B8] leading-relaxed space-y-6 text-base md:text-lg">
            <p>
              <span className="float-left font-serif-display text-7xl md:text-8xl text-[#D4AF37] leading-[0.75] mr-4 mt-2">U</span>
              -NEXX Development Group Inc. is a full-service land and housing development and consulting company specializing in acquiring, developing, and flipping underutilized and abandoned properties.
            </p>
            <p>
              The company also provides end-to-end real estate solutions for international clients seeking to invest in property without local knowledge or operational capacity — from Edmonton land assemblies to boutique villa developments in the Caribbean.
            </p>
            <p>
              Our services cover the entire lifecycle of real estate investment — from property sourcing and acquisition to development, renovation, and resale or rental management, both locally and abroad. Our flagship international project is Reign Villa Resorts — a boutique six-villa luxury resort in Canelles, St. Lucia, and the first of many Caribbean initiatives in our pipeline.
            </p>
          </div>
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* TEAMS */}
      <section data-testid="about-teams" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-6">Two teams · one platform</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#F3EFE4] leading-[0.95]">
                Boots on the ground <span className="italic text-[#D4AF37]">in every market we operate.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-[#8FA3B8] leading-relaxed">
                Real estate is local. That's why U-NEXX operates as one company with two dedicated teams — a Canadian headquarters that oversees strategy, capital, and Alberta projects, and a St. Lucia team that leads our Caribbean developments and investor liaison on the ground.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="border border-white/10 p-8 md:p-10 bg-[#0F1F35] relative">
              <div className="absolute top-6 right-6 text-[0.62rem] tracking-[0.28em] uppercase text-[#D4AF37]">Headquarters</div>
              <div className="overline mb-4">🇨🇦 Canada Team</div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-4">Edmonton, Alberta</h3>
              <p className="text-sm text-[#8FA3B8] leading-relaxed mb-6">
                Our main base of operations. Executive leadership, capital markets, investor relations, and all Alberta project execution — from Edmonton infills to distressed asset flips across the Capital Region.
              </p>
              <ul className="space-y-3 text-sm text-[#F3EFE4]">
                <li className="flex items-start gap-3"><span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" /> Strategy & capital allocation</li>
                <li className="flex items-start gap-3"><span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" /> Investor relations & reporting</li>
                <li className="flex items-start gap-3"><span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" /> Alberta development & flips</li>
                <li className="flex items-start gap-3"><span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" /> Legal, tax & compliance</li>
              </ul>
            </div>

            <div className="border border-[#D4AF37]/40 p-8 md:p-10 bg-gradient-to-br from-[#0F1F35] to-[#0A1628] relative">
              <div className="absolute top-6 right-6 text-[0.62rem] tracking-[0.28em] uppercase text-[#D4AF37]">Caribbean Ops</div>
              <div className="overline mb-4">🇱🇨 St. Lucia Team</div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-4">Canelles, St. Lucia</h3>
              <p className="text-sm text-[#8FA3B8] leading-relaxed mb-6">
                On-the-ground execution for our Caribbean portfolio. Leads site management, contractor coordination, and municipal liaison for our flagship villa project and future Caribbean initiatives.
              </p>
              <ul className="space-y-3 text-sm text-[#F3EFE4]">
                <li className="flex items-start gap-3"><span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" /> Site management & construction</li>
                <li className="flex items-start gap-3"><span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" /> Local trades & consultants</li>
                <li className="flex items-start gap-3"><span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" /> D.C.A. & municipal liaison</li>
                <li className="flex items-start gap-3"><span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" /> Investor site visits & hospitality</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section data-testid="about-principles" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-6">Principles</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#F3EFE4]">
                The four ideas that govern <span className="italic text-[#D4AF37]">every decision.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.no} delay={i}>
                <div className="border-r border-b border-white/10 p-8 md:p-10 min-h-[260px] flex flex-col justify-between">
                  <div className="font-serif-display text-3xl text-[#D4AF37]">{p.no}</div>
                  <div>
                    <h3 className="font-serif-display text-2xl text-[#F3EFE4] mb-3">{p.title}</h3>
                    <p className="text-sm text-[#8FA3B8] leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      {TIMELINE.length > 0 && (
        <section data-testid="about-timeline" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#0F1F35]">
          <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="overline mb-6">Milestones</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#F3EFE4] leading-[0.95]">
                A short history of building <span className="italic text-[#D4AF37]">forward.</span>
              </h2>
            </div>
            <div className="lg:col-span-8">
              <div className="relative">
                <div className="absolute left-0 md:left-6 top-2 bottom-2 w-px bg-white/10" />
                <div className="space-y-12">
                  {TIMELINE.map((item, i) => (
                    <Reveal key={item.year} delay={i}>
                      <div className="grid md:grid-cols-[120px_1fr] gap-6 md:gap-12 items-start pl-8 md:pl-16 relative">
                        <div className="absolute left-[-5px] md:left-[19px] top-2 w-3 h-3 border border-[#D4AF37] bg-[#0F1F35]" />
                        <div className="font-serif-display text-3xl text-[#D4AF37]">{item.year}</div>
                        <div>
                          <h3 className="font-serif-display text-2xl text-[#F3EFE4] mb-2">{item.title}</h3>
                          <p className="text-sm text-[#8FA3B8] leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* QUOTE / CTA */}
      <section data-testid="about-cta" className="py-32 md:py-40 px-6 md:px-12 lg:px-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[#F3EFE4] leading-[1.15] italic">
            "We're not in the business of selling homes. We're in the business of building futures — for the families that live in them, and the investors who back them."
          </div>
          <div className="mt-10 overline">— The U-NEXX Team</div>
          <button data-testid="about-cta-btn" onClick={onOpenConsult} className="btn-primary mt-12">
            Start a Conversation <span className="btn-arrow">→</span>
          </button>
          <div className="mt-12 text-xs text-[#8FA3B8] tracking-[0.3em] uppercase">{BRAND.founded} · {BRAND.markets}</div>
        </div>
      </section>
    </div>
  );
}
